import { MAX_CLAIM_LODGEMENT_FILE_BYTES } from '@shared/constants/claim/file-limits';
import { CLAIMANT_ROLES, type ClaimJsonData, type ContactCollection } from '@shared/types';
import { DateTime } from 'luxon';
import { z } from 'zod';

/** Bounds what the public lodgement form may persist to `Claim.jsonData`; unknown keys are stripped. */
export const claimJsonDataSchema = z.object({
    claimNotResultOfIllegalAct: z.boolean().optional(),
    questionnaireDeclarationTitle: z.string().nullable().optional(),
    questionnaireDeclarationBody: z.string().nullable().optional(),
    claimantRole: z.enum(CLAIMANT_ROLES).optional(),
    fleetLookupDriverContactId: z.uuid().optional(),
    incidentLocation: z
        .object({
            latitude: z.number().min(-90).max(90),
            longitude: z.number().min(-180).max(180),
            accuracy: z.number().min(0).optional(),
            source: z.enum(['device', 'manual']),
            capturedAt: z.string().datetime().optional(),
        })
        .optional(),
    // Display-only copy of the fleet vehicle matched at lodgement (CUR-494).
    // The authoritative link is Claim.fleetVehicleId, set server-side from the
    // signed vehicle proof — never from this snapshot.
    fleetVehicleSnapshot: z
        .object({
            registration: z.string().max(20),
            description: z.string().max(200).nullable().optional(),
            operator: z.string().max(200).nullable().optional(),
        })
        .optional(),
}) satisfies z.ZodType<ClaimJsonData>;

export const publicClaimInputSchema = z.object({
    accessKey: z.string().nullable().optional(),
    additionalInfo: z.string().nullable().optional(),
    brokerCompany: z.string().nullable().optional(),
    causeOfLoss: z.string().nullable().optional(),
    addressLine1OfLoss: z.string().nullable().optional(),
    addressLine2OfLoss: z.string().nullable().optional(),
    suburbOfLoss: z.string().nullable().optional(),
    stateOfLoss: z.string().nullable().optional(),
    postcodeOfLoss: z.string().nullable().optional(),
    claimOccurredAt: z.union([z.date(), z.string()]).nullable().optional(),
    contacts: z.custom<ContactCollection>().default({}),
    dtClosed: z.union([z.date(), z.string()]).nullable().optional(),
    files: z
        .record(
            z.string(),
            z.object({
                categories: z.array(z.string()).default([]).optional(),
                createdAt: z.string().default(() => DateTime.now().toISO()),
                id: z.string(),
                internalName: z.string().default('').optional(),
                isDeleted: z.boolean().default(false).optional(),
                key: z.string(),
                mimeType: z.string().default('').optional(),
                name: z.string(),
                // Defence-in-depth bound on the client-reported size. The authoritative
                // byte-level control is the `content-length-range` on the presigned POST
                // (see getUploadPresignedUrl `claimLodgement` purpose); this rejects
                // obviously-bogus submissions and bounds the persisted value.
                size: z.number().max(MAX_CLAIM_LODGEMENT_FILE_BYTES),
                uploadedById: z.number(),
            }),
        )
        .default({}),
    formData: z.any().optional(),
    jsonData: claimJsonDataSchema.default({}),
    orgId: z.bigint(),
    pdsVersionId: z.bigint(),
    phABN: z.string().nullable().optional(),
    phAccountBsb: z.string().nullable().optional(),
    phAccountName: z.string().nullable().optional(),
    phAccountNo: z.string().nullable().optional(),
    phCompany: z.string().nullable().optional(),
    phIsRegisteredForGst: z.boolean().nullable().optional(),
    phITC: z.string().nullable().optional(),
    policyIssuedOn: z.union([z.date(), z.string()]).nullable().optional(),
    policyName: z.string().nullable().optional(),
    policyNo: z.string().nullable().optional(),
    policyOtherInfo: z.string().nullable().optional(),
    policyTermFrom: z.union([z.date(), z.string()]).nullable().optional(),
    policyTermTo: z.union([z.date(), z.string()]).nullable().optional(),
    productTypeCustomFieldsData: z.record(z.string(), z.any()).optional(),
    productTypeId: z.bigint().nullable().optional(),
    questionnaireId: z.bigint().nullable().optional(),
    // Client-minted idempotency key: one fresh UUID per new form state. A duplicate
    // submit of the same state collides on (orgId, submissionId) server-side → CONFLICT.
    submissionId: z.string().uuid().optional(),
    // Signed vehicle proof from the fleet lodgement lookup (CUR-494). Opaque to
    // the client; the server verifies it and sets Claim.fleetVehicleId. An
    // invalid/expired/foreign proof is ignored — the claim simply has no link.
    fleetVehicleProof: z.string().min(1).max(2048).nullable().optional(),
});
